import { newVersionReady } from './release/newVersion.ts';
/**
 * The address bar is the state: real URLs, the History API, no hash. An Agora
 * is part of the address -- `/h/<agoraId>/<section>/...` -- so a link says
 * which company it belongs to, not only which conversation.
 *
 * Five sections. Today is where a person lands, because it is the one thing
 * that makes this one product rather than three
 * (https://github.com/wtfalch/agora/issues/215).
 *
 *     /h/<agoraId>                              Today
 *     /h/<agoraId>/mail/<mailboxId>/<threadId>
 *     /h/<agoraId>/chat/<roomId>/<threadId>
 *     /h/<agoraId>/calendar/<date>/<eventId>
 *     /h/<agoraId>/files                        My files
 *     /h/<agoraId>/files/shared                 Shared with me
 *     /h/<agoraId>/tasks/<listId>/<taskId>      `listId` is `mine` for My tasks
 *     /h/<agoraId>/tasks/<listId>/board         a list drawn as a board
 *
 * Each of Mail, Chat, Calendar and Files also has a `search` place of its
 * own -- `/h/<agoraId>/mail/search/<query>`, and the same shape for chat,
 * calendar and files -- the full-results view Enter opens from the header
 * search when nothing is highlighted (agora#269). `search`, like mail's own
 * `new` and files' own `shared`, is a reserved first segment no real id ever
 * is.
 *
 * The location types are Agora's own. They used to be imported from the mail
 * and chat packages' React entries, which Agora is leaving: those packages are
 * SDKs to it now, and the shape of Agora's address is Agora's business.
 *
 * `eventId` in a chat location is deliberately not in the path. It is the
 * message "go to message" asks the timeline to scroll to -- a moment, not a
 * place -- and putting it in the address would make every such jump a new
 * history entry pointing at a message that may since have scrolled away.
 */

export type Section = 'today' | 'mail' | 'chat' | 'calendar' | 'files' | 'tasks';

export const SECTIONS: readonly Section[] = ['today', 'mail', 'chat', 'calendar', 'files', 'tasks'];

export type MailLocation = {
  mailboxId: string | null;
  threadId: string | null;
  /** Set for exactly one navigation -- landing on `/h/<agoraId>/mail/new`
   *  -- as a one-shot trigger to open the composer; `Mail` reads it once
   *  and never writes it back into the address, the same "a mode, not a
   *  route" rule its own docblock states for compose. */
  compose?: boolean;
  /** Seeds the composer's To field; read with `compose`, from
   *  `/h/<agoraId>/mail/new/<address>` (the person card's Mail button). */
  to?: string;
  /** The full-results view Enter opens when nothing is highlighted in the
   *  header search (agora#269) -- every thread `search()` finds for this
   *  text, not the header panel's own capped preview. Set instead of
   *  `mailboxId`/`threadId`, never alongside them; `Mail.tsx` checks this
   *  first. */
  query?: string;
};
export type ChatLocation = {
  roomId: string | null;
  threadId: string | null;
  eventId: string | null;
  /** The same full-results view as `MailLocation.query`, for chat messages
   *  (agora#269) -- set only when `@wtfalch/chat`'s `ChatClient.search`
   *  exists, which it does as of 0.4.0. */
  query?: string;
};
/** `date` is a local `YYYY-MM-DD`, the day the week view is anchored on. */
export type CalendarLocation = {
  date: string | null;
  eventId: string | null;
  /** The event uid whose call this address opens
   *  (`/h/<agoraId>/calendar/call/<uid>`). Set instead of `date`/`eventId`. */
  callUid?: string;
  /** The new-event form (`/h/<agoraId>/calendar/new[/<date>]`); `date` is
   *  the week it was opened from. */
  creating?: boolean;
  /** The same full-results view as `MailLocation.query`, for calendar
   *  events (agora#269). */
  query?: string;
};
/** `tab` is which list is open -- there is no id in a files address the way
 *  a mailbox or a room has one, only the choice of list. */
export type FilesLocation = {
  tab: 'mine' | 'shared';
  /** The same full-results view as `MailLocation.query` (agora#269), for
   *  files' `FilesSource.search`. Set instead of `tab`, never alongside it;
   *  `Files.tsx` checks this first, the same rule `MailLocation.query`'s own
   *  doc states. */
  query?: string;
};

/** `listId` is `mine` (My tasks) or a shared list's id. `taskId` opens one task
 *  over its list. `board` draws a shared list as columns instead of rows;
 *  `board` is a reserved second segment no task id ever is. */
export type TasksLocation = {
  listId: string;
  taskId: string | null;
  board?: boolean;
};

export type AgoraRoute =
  | { kind: 'agora'; agoraId: string; section: 'today' }
  | { kind: 'agora'; agoraId: string; section: 'mail'; location: MailLocation }
  | { kind: 'agora'; agoraId: string; section: 'chat'; location: ChatLocation }
  | { kind: 'agora'; agoraId: string; section: 'calendar'; location: CalendarLocation }
  | { kind: 'agora'; agoraId: string; section: 'files'; location: FilesLocation }
  | { kind: 'agora'; agoraId: string; section: 'tasks'; location: TasksLocation };

/** A person's own preferences, not a company's -- reached from the account
 *  menu rather than the place rail, so it is not a `Section` and carries no
 *  `AgoraShell` chrome, the same as `home`/`add`/`callback` (agora#258). */
export type SettingsRoute = { kind: 'settings'; agoraId: string };

export type Route =
  | { kind: 'home' }
  | { kind: 'add' }
  | { kind: 'callback' }
  /** `/call/guest#<secret>`: a guest's join page, outside sign-in. */
  | { kind: 'guest-call' }
  | SettingsRoute
  | AgoraRoute;

const NO_MAIL: MailLocation = { mailboxId: null, threadId: null };
const NO_CHAT: ChatLocation = { roomId: null, threadId: null, eventId: null };
const NO_CALENDAR: CalendarLocation = { date: null, eventId: null };
const NO_FILES: FilesLocation = { tab: 'mine' };
const NO_TASKS: TasksLocation = { listId: 'mine', taskId: null };

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A hand-typed address can hold a bare `%` that is not an escape, and
 *  `decodeURIComponent` throws on it; keep that segment as typed instead. */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** Read the current path. Anything that is not a known shape falls back to
 *  `home` rather than a dead end -- an old or mistyped link should land
 *  somewhere with a way forward. */
export function parse(pathname: string): Route {
  const parts = pathname.split('/').filter(Boolean).map(decodeSegment);

  if (parts.length === 0) return { kind: 'home' };
  if (parts.length === 1 && parts[0] === 'add') return { kind: 'add' };
  if (parts.length === 1 && parts[0] === 'callback') return { kind: 'callback' };
  if (parts.length === 2 && parts[0] === 'call' && parts[1] === 'guest') {
    return { kind: 'guest-call' };
  }

  const [h, agoraId, section, first, second] = parts;
  if (h !== 'h' || !agoraId) return { kind: 'home' };

  if (section === 'settings') {
    return { kind: 'settings', agoraId };
  }
  if (section === undefined || section === 'today') {
    return { kind: 'agora', agoraId, section: 'today' };
  }
  if (section === 'mail') {
    if (first === 'new') {
      return {
        kind: 'agora',
        agoraId,
        section: 'mail',
        location: {
          mailboxId: null,
          threadId: null,
          compose: true,
          ...(second ? { to: second } : {}),
        },
      };
    }
    // `/h/<agoraId>/mail/search/<query>` -- the full-results view Enter
    // opens with nothing highlighted in the header search (agora#269).
    // Reserved the same way `new` already is: a real mailbox id is opaque,
    // never this literal string.
    if (first === 'search') {
      return {
        kind: 'agora',
        agoraId,
        section: 'mail',
        location: { mailboxId: null, threadId: null, query: second ?? '' },
      };
    }
    return {
      kind: 'agora',
      agoraId,
      section: 'mail',
      location: { mailboxId: first ?? null, threadId: first ? (second ?? null) : null },
    };
  }
  if (section === 'chat') {
    if (first === 'search') {
      return {
        kind: 'agora',
        agoraId,
        section: 'chat',
        location: { roomId: null, threadId: null, eventId: null, query: second ?? '' },
      };
    }
    return {
      kind: 'agora',
      agoraId,
      section: 'chat',
      location: { roomId: first ?? null, threadId: first ? (second ?? null) : null, eventId: null },
    };
  }
  if (section === 'calendar') {
    if (first === 'search') {
      return {
        kind: 'agora',
        agoraId,
        section: 'calendar',
        location: { date: null, eventId: null, query: second ?? '' },
      };
    }
    if (first === 'call' && second) {
      return {
        kind: 'agora',
        agoraId,
        section: 'calendar',
        location: { date: null, eventId: null, callUid: second },
      };
    }
    if (first === 'new') {
      return {
        kind: 'agora',
        agoraId,
        section: 'calendar',
        location: {
          date: second && DATE.test(second) ? second : null,
          eventId: null,
          creating: true,
        },
      };
    }
    const date = first && DATE.test(first) ? first : null;
    return {
      kind: 'agora',
      agoraId,
      section: 'calendar',
      location: { date, eventId: date ? (second ?? null) : null },
    };
  }
  if (section === 'files') {
    if (first === 'search') {
      return {
        kind: 'agora',
        agoraId,
        section: 'files',
        location: { tab: 'mine', query: second ?? '' },
      };
    }
    return {
      kind: 'agora',
      agoraId,
      section: 'files',
      location: { tab: first === 'shared' ? 'shared' : 'mine' },
    };
  }

  if (section === 'tasks') {
    const listId = first || 'mine';
    if (second === 'board') {
      return {
        kind: 'agora',
        agoraId,
        section: 'tasks',
        location: { listId, taskId: null, board: true },
      };
    }
    return {
      kind: 'agora',
      agoraId,
      section: 'tasks',
      location: { listId, taskId: second ?? null },
    };
  }

  return { kind: 'home' };
}

export function pathForHome(): string {
  return '/';
}

export function pathForAdd(): string {
  return '/add';
}

export function pathForCallback(): string {
  return '/callback';
}

const base = (agoraId: string) => `/h/${encodeURIComponent(agoraId)}`;

/** Where an Agora opens: Today. */
export function pathForAgora(agoraId: string): string {
  return base(agoraId);
}

export function pathForSection(agoraId: string, section: Section): string {
  return section === 'today' ? base(agoraId) : `${base(agoraId)}/${section}`;
}

/** A person's own preferences for this company's agora (agora#258). */
export function pathForSettings(agoraId: string): string {
  return `${base(agoraId)}/settings`;
}

/** Encoded so an id containing `/` (an opaque mailbox id, say) round-trips
 *  through `parse` rather than changing the path's shape. */
export function pathForMail(agoraId: string, location: MailLocation = NO_MAIL): string {
  const root = `${base(agoraId)}/mail`;
  if (location.query !== undefined) return `${root}/search/${encodeURIComponent(location.query)}`;
  if (!location.mailboxId) return root;
  const withMailbox = `${root}/${encodeURIComponent(location.mailboxId)}`;
  return location.threadId
    ? `${withMailbox}/${encodeURIComponent(location.threadId)}`
    : withMailbox;
}

/** Today's "Write" button and any other link that wants to land straight in
 *  the composer -- `mailboxId`/`threadId` are meaningless here, so this
 *  builds only the `new` path rather than taking a `MailLocation`. */
export function pathForMailCompose(agoraId: string, to?: string): string {
  return `${base(agoraId)}/mail/new${to ? `/${encodeURIComponent(to)}` : ''}`;
}

/** A Matrix event id, which is what a thread id is, can contain `/`. */
export function pathForChat(agoraId: string, location: ChatLocation = NO_CHAT): string {
  const root = `${base(agoraId)}/chat`;
  if (location.query !== undefined) return `${root}/search/${encodeURIComponent(location.query)}`;
  if (!location.roomId) return root;
  const withRoom = `${root}/${encodeURIComponent(location.roomId)}`;
  return location.threadId ? `${withRoom}/${encodeURIComponent(location.threadId)}` : withRoom;
}

/** A uid of exactly `.` or `..` has no path segment that both round-trips
 *  and survives URL normalisation: `encodeURIComponent` leaves either
 *  untouched (neither is in its reserved set), and the URL Standard's own
 *  dot-segment removal collapses the *percent-encoded* form too -- it
 *  defines a single-dot segment as `.` or an ASCII case-insensitive match
 *  for `%2e`, and a double-dot segment the same way for `..` -- so escaping
 *  the dots buys nothing (`new URL('.../call/%2E').pathname` still drops
 *  `call` entirely, same as a literal `.`). Collapsing silently opened the
 *  plain week view instead of the call it named (agora#392); refusing to
 *  build the link at all is the only response that does not hand out a
 *  broken one. FindTime only ever issues UUID uids today, so this should
 *  never actually throw. */
function isDotSegment(uid: string): boolean {
  return uid === '.' || uid === '..';
}

export function pathForCalendar(agoraId: string, location: CalendarLocation = NO_CALENDAR): string {
  const root = `${base(agoraId)}/calendar`;
  if (location.callUid !== undefined) {
    if (isDotSegment(location.callUid)) {
      throw new Error(
        `pathForCalendar: a call link cannot be built for uid ${JSON.stringify(location.callUid)}`,
      );
    }
    return `${root}/call/${encodeURIComponent(location.callUid)}`;
  }
  if (location.query !== undefined) return `${root}/search/${encodeURIComponent(location.query)}`;
  if (location.creating) return location.date ? `${root}/new/${location.date}` : `${root}/new`;
  if (!location.date) return root;
  const withDate = `${root}/${location.date}`;
  return location.eventId ? `${withDate}/${encodeURIComponent(location.eventId)}` : withDate;
}

/** No id to encode -- `tab` is one of two fixed words, not an opaque id the
 *  way a mailbox or a room is. */
export function pathForFiles(agoraId: string, location: FilesLocation = NO_FILES): string {
  const root = `${base(agoraId)}/files`;
  if (location.query !== undefined) return `${root}/search/${encodeURIComponent(location.query)}`;
  return location.tab === 'shared' ? `${root}/shared` : root;
}

export function pathForTasks(agoraId: string, location: TasksLocation = NO_TASKS): string {
  const root = `${base(agoraId)}/tasks`;
  if (location.listId === 'mine' && !location.taskId && !location.board) return root;
  const withList = `${root}/${encodeURIComponent(location.listId)}`;
  if (location.board) return `${withList}/board`;
  return location.taskId ? `${withList}/${encodeURIComponent(location.taskId)}` : withList;
}

/** Any `AgoraRoute` to the path that opens it -- what a combined search
 *  result's own `route` (`search/types.ts`'s `SearchRow`) is for: one row
 *  shape whose `route` a caller hands straight to `go` without first asking
 *  which section it belongs to. */
export function pathForRoute(route: AgoraRoute): string {
  if (route.section === 'today') return pathForAgora(route.agoraId);
  if (route.section === 'mail') return pathForMail(route.agoraId, route.location);
  if (route.section === 'chat') return pathForChat(route.agoraId, route.location);
  if (route.section === 'calendar') return pathForCalendar(route.agoraId, route.location);
  if (route.section === 'tasks') return pathForTasks(route.agoraId, route.location);
  return pathForFiles(route.agoraId, route.location);
}

/**
 * An Agora room address: an absolute `http(s)` URL on `currentOrigin` whose
 * path `parse`s to a chat route naming a room -- a link to any other
 * section, a bare room-less `/chat`, a different origin, or a non-`http(s)`
 * scheme (a stray `javascript:`, say) is not "a room" to open in place of a
 * new tab, so all of those answer `null` here and are left as an ordinary
 * link (agora#222). Every Agora is reachable only at its own address
 * (foundry assigns the hostname per company), so there is no second, fixed
 * host a link could otherwise name alongside `currentOrigin`.
 *
 * `currentOrigin` is a parameter, not a `window.location` read, so this stays
 * callable with no DOM -- the same "pure, testable without a DOM" rule this
 * file's own doc states for `parse` and every `pathFor*` above.
 */
export function parseRoomUrl(
  href: string,
  currentOrigin: string,
): (AgoraRoute & { section: 'chat' }) | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  if (url.origin !== currentOrigin) return null;
  const route = parse(url.pathname);
  if (route.kind !== 'agora' || route.section !== 'chat' || !route.location.roomId) return null;
  return route;
}

/**
 * Navigates the whole app to `path`, the same way `App.tsx`'s own `go` does
 * -- pushes the entry, then fires the `popstate` event `App`'s listener is
 * already watching so its `path` state (and every route below it) picks the
 * new address up. A room link or an invitation's "Open in Calendar" (agora#222)
 * is drawn deep inside Mail and Chat, several components below where `go`
 * lives; reaching it from there would mean threading a callback prop through
 * every component in between, several of which this ticket does not
 * otherwise touch. Dispatching the same event `App` already listens for
 * reaches it with no prop drilling and no new plumbing.
 */
export function navigate(path: string): void {
  if (newVersionReady()) {
    window.location.assign(path);
    return;
  }
  window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * Whether a click on a room link should be intercepted for `navigate`
 * (agora#222) -- a plain primary click, button `0`, with no modifier held.
 * Cmd/Ctrl-click and a middle-click (button `1`) both mean "open in a new
 * tab," Shift-click means "open in a new window," and Alt-click means
 * "download" -- every one of them an intent the browser already knows how
 * to honour for a plain `<a href>`, and every one of them silently taken
 * away the moment a click handler calls `preventDefault` unconditionally
 * (a refuter finding: all four were being hijacked into an in-app
 * navigation instead). Structurally typed against `MouseEvent`'s own shape
 * rather than importing React's event type, so this stays callable from
 * anywhere a click is read, DOM or React alike.
 */
export function isPlainClick(event: {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}
